/**
 * Engine API client — Strategy Automation (SUPERBETA-7)
 *
 * A TYPED read/write layer over the signal engine's REST surface. It owns the
 * shapes, the normalization and the demo/live classification; it does NOT own
 * the transport. Every request goes through services/signalsApi.js, which is
 * the single place the base URL (REACT_APP_SIGNALS_API_URL) and the member's
 * Firebase bearer token are read from. Nothing here hardcodes a URL, a token,
 * or a wallet, and nothing here signs anything.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * GROUND TRUTH: ENGINE_CONTRACTS_FOR_FRONTEND_2026-08-05.md. Each item in that
 * document is marked RATIFIED or PLAN, and this file honours the distinction:
 *
 *   RATIFIED — built against, called live:
 *     §1b sizingFloors        → GET /status.sizingFloors
 *     §2  pendingExits        → GET /status.pendingExits
 *     §3  staking gate        → GET /status.staking
 *     the existing /status + /config surface (risk limits, pair allowlist,
 *     enabled, and the Phase-1 allocations shape, which PERSISTS only)
 *
 *   PLAN — typed interfaces and stubs only, never called:
 *     §1 Phase 2 shadow sizing, §1 Phase 3 arming.
 *     See PLAN STUBS at the bottom. They throw. They make no request.
 * ──────────────────────────────────────────────────────────────────────────
 *
 * THREE RULES THIS FILE ENFORCES, because they are the ones that go wrong:
 *
 *  1. ABSENCE IS A STATE, NOT A ZERO. A field the engine did not send comes
 *     back null (or [] where the contract says absent-means-empty). It is never
 *     defaulted to a plausible number. `sizingFloors` absent means "floors
 *     unknown" and the UI renders no minimums (§1b rule 6).
 *
 *  2. THE ENGINE COMPUTES, THE FRONTEND RENDERS. `staking.passed` and every
 *     `minPct` are taken as given. The one derivation here is the fail-closed
 *     clamp in §3 rule 2: an unverifiable gate is never `passed`.
 *
 *  3. DEMO IS NOT AN ERROR. Unreachable or unconfigured is a first-class state
 *     the UI labels; it is classified here (`isUnreachableError`) so callers
 *     never have to string-match a message to tell "no engine" from "engine
 *     said no".
 *
 * Moves no money. Signs nothing. Arms nothing.
 */

import signalsApi, { isConfigured } from "./signalsApi";

/* ── types (JSDoc — this codebase is JS, so the types are the docs) ───────── */

/**
 * @typedef {Object} EngineSizingFloor  §1b — per-pair minimum viable allocation.
 * @property {string} pair              display/venue pair name as the engine sent it
 * @property {number} minPct            minimum % of balance, ROUNDED UP by the engine
 * @property {number|null} minPositionUsd  gTrade's live minimum for this pair
 * @property {number|null} minCollateralUsd
 * @property {number|null} leverage     the effective leverage used in the math
 */

/**
 * @typedef {Object} EngineSizingFloors
 * @property {number|null} balanceUsd   collateral the floors were computed against
 * @property {number|null} computedAtMs
 * @property {EngineSizingFloor[]} floors
 */

/**
 * @typedef {Object} EngineAllocation
 * @property {number} pct               0–100; non-cash allocations sum to ≤ 100
 * @property {number|null} leverage     server-clamped to the 2x ceiling
 */

/**
 * @typedef {Object} EngineRiskConfig
 * @property {number|null} maxLeverage
 * @property {number|null} maxCollateralPctPerTrade
 * @property {string[]} pairAllowlist   VERBATIM engine strings — see normalizeRiskConfig
 * @property {boolean|null} enabled
 * @property {Object<string, EngineAllocation>} allocations  keyed by wire key
 */

/**
 * @typedef {Object} EngineOpenPosition
 * @property {string} pair
 * @property {boolean|null} long
 * @property {number|null} sizeUsd
 * @property {number|null} collateralUsd
 */

/**
 * @typedef {Object} EnginePendingExit  §2 — "your exit is executing", not "is coming".
 * @property {string} id
 * @property {string} pair
 * @property {"long"|"short"|null} side       direction of the position BEING CLOSED
 * @property {"CLOSE"|"FLIP_LONG"|"FLIP_SHORT"|null} intent
 * @property {"signal"|"manual"|"risk"|null} reason   CLOSED ENUM — never free text
 * @property {"submitted"|"filled"|"cancelled"|"failed"|null} state
 * @property {number|null} submittedAtMs
 * @property {number|null} resolvedAtMs
 * @property {string|null} txHash
 * @property {number|null} tradeIndex
 */

/**
 * @typedef {Object} EngineStakingGate  §3 — the member-level slot pool (ARC-42).
 *
 * The ARC-42 fields are the live contract; the Aug-5 ones below them are the
 * deprecated mirror the engine keeps for one release. Every COUNT is nullable
 * and null means UNKNOWN, never zero — see normalizeStaking.
 *
 * @property {"active"|"closing_only"|"blocked"|null} gate  ENGINE-COMPUTED
 * @property {number|null} slotsTotal
 * @property {number|null} slotsUsed
 * @property {number|null} slotsAvailable
 * @property {boolean} openPositionRiding
 * @property {Array<{wallet: string|null, pair: string}>} unbackedPairs
 * @property {Array<{address: string, staked: number|null, label: string|null}>} perWallet
 * @property {boolean} indexHealthy     false ⇒ the counts are absent, not zero
 * @property {number|null} asOfBlock
 * @property {boolean} gateEnabled      DEPRECATED (Aug-5 shape)
 * @property {number|null} staked       DEPRECATED
 * @property {number|null} threshold    DEPRECATED
 * @property {string|null} unit         DEPRECATED
 * @property {boolean} passed           DEPRECATED mirror of `gate === "active"`
 * @property {number|null} checkedAtMs
 * @property {"onchain"|"cached"|"unavailable"} source
 */

/**
 * @typedef {Object} EngineStatus
 * @property {string|null} wallet
 * @property {boolean} delegationOk     delegate status for THIS wallet
 * @property {boolean|null} enabled
 * @property {number|null} collateralUsdc
 * @property {EngineRiskConfig|null} riskConfig   null before enrollment
 * @property {Object} pairMeta          read-only gTrade limits echo; {} if unavailable
 * @property {EngineOpenPosition[]} openPositions
 * @property {Array} recentSignals
 * @property {EngineSizingFloors|null} sizingFloors  null ⇒ floors UNKNOWN (§1b r6)
 * @property {EngineStakingGate|null} staking        null ⇒ engine doesn't serve it yet
 * @property {EnginePendingExit[]} pendingExits      absent ⇒ [] (§2), never an error
 */

/* ── small coercions ─────────────────────────────────────────────────────── */

const num = (n) => (n == null || n === "" || !Number.isFinite(Number(n)) ? null : Number(n));
const str = (s) => (typeof s === "string" && s.trim() ? s.trim() : null);
const bool = (b) => (typeof b === "boolean" ? b : null);
const oneOf = (v, allowed) => (allowed.includes(v) ? v : null);

/**
 * The engine's pair normalization: "BTC/USD" → "BTCUSD" (uppercase,
 * alphanumerics only). Allocations, floors and the pair allowlist all arrive
 * keyed this way, so every lookup in the app goes through this one function.
 * `allocationModel.wireKey` re-exports it — do not fork a second copy.
 */
export const normalizePairKey = (pair) => (pair || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

/* ── allocations: the two-shapes adapter ─────────────────────────────────────
   The contract's Phase-1 shape (§1) is `Array<{pair, pct}>`, and ARC-50
   verified against the engine's users.ts that this is what the live endpoint
   both takes and returns — an earlier note here claimed it was a map, which is
   what made every allocation save 400 and every reload read back 0%.
   Both shapes are still READ here so a rollback or an older engine cannot break
   the tab, and neither is guessed at: an entry without a usable pct is dropped
   rather than defaulted to 0, which would read on screen as "the member set
   this pair to nothing". Writes go out as the array — see setAllocations. */

/**
 * @param {Array<{pair:string,pct:number,leverage?:number}>|Object|null|undefined} raw
 * @returns {Object<string, EngineAllocation>} keyed by wire key; {} when absent
 */
export const toAllocationMap = (raw) => {
  const out = {};
  if (!raw || typeof raw !== "object") return out;

  const put = (pair, pct, leverage) => {
    const key = normalizePairKey(pair);
    const p = num(pct);
    if (!key || p === null) return;
    out[key] = { pct: p, leverage: num(leverage) };
  };

  if (Array.isArray(raw)) {
    raw.forEach((a) => put(a?.pair, a?.pct, a?.leverage));
    return out;
  }
  Object.keys(raw).forEach((key) => {
    const a = raw[key];
    /* Tolerate both { BTCUSD: {pct} } and the flatter { BTCUSD: 30 }. */
    if (a != null && typeof a === "object") put(key, a.pct, a.leverage);
    else put(key, a, undefined);
  });
  return out;
};

/**
 * The contract's Phase-1 wire shape, for the day the engine asks for it.
 * @param {Object<string, EngineAllocation>} map
 * @returns {Array<{pair:string, pct:number}>}
 */
export const toAllocationArray = (map) =>
  Object.keys(map || {}).map((pair) => ({ pair, pct: Number(map[pair]?.pct) || 0 }));

/* ── normalizers (pure — every one of these is unit-tested on fixtures) ───── */

/** §1b. Returns null when the engine sent no usable floors: "floors unknown". */
export const normalizeSizingFloors = (raw) => {
  const list = Array.isArray(raw?.floors) ? raw.floors : null;
  if (!list) return null;

  const floors = [];
  list.forEach((f) => {
    const pair = str(f?.pair);
    const minPct = num(f?.minPct);
    /* An entry with no comparable minimum is not a floor. Keeping it would put
       a marker on the slider with nothing behind it. */
    if (!pair || minPct === null) return;
    floors.push({
      pair,
      minPct,
      minPositionUsd: num(f?.minPositionUsd),
      minCollateralUsd: num(f?.minCollateralUsd),
      leverage: num(f?.leverage),
    });
  });

  if (floors.length === 0) return null;
  return { balanceUsd: num(raw?.balanceUsd), computedAtMs: num(raw?.computedAtMs), floors };
};

/** A count from the wire: absent, negative or unparseable ⇒ null (= unknown). */
const slotCount = (n) => {
  const v = num(n);
  return v === null || v < 0 ? null : v;
};

/**
 * §3 — the member-level slot pool (ARC-42), plus the deprecated Aug-5 mirror.
 *
 * TWO RULES, AND THEY ARE THE WHOLE POINT OF THIS FUNCTION:
 *
 *  1. `gate` IS READ, NEVER DERIVED. The engine owns the lifecycle
 *     (active / closing_only / blocked) because only it knows whether a
 *     position is riding. A client that recomputed it from counts would
 *     disagree with the engine at exactly the moment that matters.
 *
 *  2. A COUNT WE DON'T HAVE IS NOT ZERO. ARC-42 omits the counts when the
 *     stake index is unhealthy, precisely so a client cannot mistake failure
 *     for "nothing staked". We hold the same line from this side: with
 *     `indexHealthy` false — including when the engine never sent the field —
 *     every count is nulled here even if one leaked onto the wire. Null means
 *     the caller must say "we couldn't read this", and per ARC-53 it must lock
 *     rather than unlock.
 */
export const normalizeStaking = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const source = oneOf(raw.source, ["onchain", "cached", "unavailable"]) || "unavailable";

  /* Absent ⇒ false. An engine that does not tell us the index is healthy has
     not told us it is, and this is the fail-closed side of that. */
  const indexHealthy = bool(raw.indexHealthy) ?? false;
  const count = (n) => (indexHealthy ? slotCount(n) : null);

  const unbackedPairs = (Array.isArray(raw.unbackedPairs) ? raw.unbackedPairs : [])
    .map((p) => ({ wallet: str(p?.wallet), pair: str(p?.pair) }))
    .filter((p) => p.pair);

  const perWallet = (Array.isArray(raw.perWallet) ? raw.perWallet : [])
    .map((w) => ({ address: str(w?.address), staked: count(w?.staked), label: str(w?.label) }))
    .filter((w) => w.address);

  return {
    /* ── ARC-42: the live contract ─────────────────────────────────────── */
    gate: oneOf(raw.gate, ["active", "closing_only", "blocked"]),
    slotsTotal: count(raw.slotsTotal),
    slotsUsed: count(raw.slotsUsed),
    slotsAvailable: count(raw.slotsAvailable),
    openPositionRiding: bool(raw.openPositionRiding) ?? false,
    unbackedPairs,
    perWallet,
    indexHealthy,
    asOfBlock: num(raw.asOfBlock),

    /* ── Aug-5 shape: DEPRECATED, kept for one release ─────────────────── */
    gateEnabled: bool(raw.gateEnabled) ?? false,
    staked: num(raw.staked),
    threshold: num(raw.threshold),
    unit: str(raw.unit),
    /* §3 rule 2 — a gate that cannot verify must not let anyone through. */
    passed: source === "unavailable" ? false : bool(raw.passed) ?? false,
    checkedAtMs: num(raw.checkedAtMs),
    source,
  };
};

/**
 * The wallet registry (ARC-41), normalized for the tab (ARC-46).
 *
 * A wallet with no readable address is DROPPED rather than rendered as a blank
 * row: the address is the only thing that identifies which wallet a Remove
 * would detach, and a row you cannot identify is a row you must not act on.
 *
 * `automatedPairs` stays in the engine's normalized form here — this is the
 * wire layer. Mapping "BTCUSD" back to "BTC/USD" happens at the point of
 * render (walletRegistry.displayEngineText), so the count and the comparison
 * both keep working on the engine's own keys.
 */
export const normalizeWallets = (raw) => {
  const list = Array.isArray(raw) ? raw : Array.isArray(raw?.wallets) ? raw.wallets : null;
  if (!list) return [];
  return list
    .map((w) => ({
      address: str(w?.address),
      label: str(w?.label),
      /* Absent ⇒ false. "The engine did not say this is the primary" is not the
         same claim as "it is", and only the primary carries the ARC-48 block. */
      isPrimary: bool(w?.isPrimary) ?? false,
      verifiedAt: num(w?.verifiedAt),
      chainId: num(w?.chainId),
      automatedPairs: (Array.isArray(w?.automatedPairs) ? w.automatedPairs : [])
        .map((p) => str(p))
        .filter(Boolean),
    }))
    .filter((w) => w.address);
};

/** §2. Absent means [], never an error. `reason` and `state` are closed enums. */
export const normalizePendingExits = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((e) => e && (str(e.id) || str(e.pair)))
    .map((e) => ({
      id: str(e.id) || `${str(e.pair)}:${num(e.submittedAtMs) ?? 0}`,
      pair: str(e.pair) || "—",
      side: oneOf(e.side, ["long", "short"]),
      intent: oneOf(e.intent, ["CLOSE", "FLIP_LONG", "FLIP_SHORT"]),
      /* Free text here would be member-visible copy nobody reviewed. Anything
         off the enum is dropped to null rather than rendered. */
      reason: oneOf(e.reason, ["signal", "manual", "risk"]),
      state: oneOf(e.state, ["submitted", "filled", "cancelled", "failed"]),
      submittedAtMs: num(e.submittedAtMs),
      resolvedAtMs: num(e.resolvedAtMs),
      txHash: str(e.txHash),
      tradeIndex: num(e.tradeIndex),
    }));
};

export const normalizeRiskConfig = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  return {
    maxLeverage: num(raw.maxLeverage),
    maxCollateralPctPerTrade: num(raw.maxCollateralPctPerTrade),
    /* Kept VERBATIM, not wire-keyed. The engine accepts either form and echoes
       back what it was given, and RiskConfigCard's chips match on the display
       string ("BTC/USD") — normalizing here would silently unselect every chip
       the member has saved. Callers that need a key run it through
       normalizePairKey at the comparison, which is where it belongs. */
    pairAllowlist: Array.isArray(raw.pairAllowlist)
      ? raw.pairAllowlist.filter((p) => typeof p === "string" && p.trim())
      : [],
    enabled: bool(raw.enabled),
    allocations: toAllocationMap(raw.allocations),
  };
};

const normalizePositions = (raw) =>
  (Array.isArray(raw) ? raw : [])
    .filter((p) => p && str(p.pair))
    .map((p) => ({
      pair: str(p.pair),
      long: bool(p.long),
      sizeUsd: num(p.sizeUsd),
      collateralUsd: num(p.collateralUsd),
    }));

/**
 * GET /status → EngineStatus. Field paths the tab already reads (riskConfig,
 * openPositions, pairMeta, sizingFloors) keep their names, so this is a
 * tightening of the existing shape rather than a new one.
 * @returns {EngineStatus}
 */
export const normalizeStatus = (raw) => {
  const s = raw && typeof raw === "object" ? raw : {};
  return {
    wallet: str(s.wallet),
    delegationOk: s.delegationOk === true,
    enabled: bool(s.enabled),
    collateralUsdc: num(s.collateralUsdc),
    riskConfig: normalizeRiskConfig(s.riskConfig),
    pairMeta: s.pairMeta && typeof s.pairMeta === "object" ? s.pairMeta : {},
    openPositions: normalizePositions(s.openPositions),
    recentSignals: Array.isArray(s.recentSignals) ? s.recentSignals : [],
    sizingFloors: normalizeSizingFloors(s.sizingFloors),
    staking: normalizeStaking(s.staking),
    pendingExits: normalizePendingExits(s.pendingExits),
  };
};

/* ── error classification ─────────────────────────────────────────────────
   Demo mode is decided on these, not on message text. "The engine isn't there"
   and "the engine answered and said no" are different states and only one of
   them may put the tab into labelled demo data. */

/** No base URL configured, or the request never reached the engine. */
export const isUnreachableError = (e) => e?.code === "NOT_CONFIGURED" || e?.code === "NETWORK";

/** 404 before enrollment — the engine IS reachable, there is just no record yet. */
export const isNotEnrolledError = (e) => e?.code === "HTTP_404";

/* ── the RATIFIED calls ───────────────────────────────────────────────────── */

/** Is a base URL configured at all? False ⇒ the tab starts in demo mode. */
export const isEngineConfigured = () => isConfigured();

/**
 * GET /delegate-address — the on-chain constants the tab needs to build the
 * Revoke transaction. Read-only, and safe before enrollment.
 * @returns {Promise<{delegateAddress:string, diamond:string, chainId:number,
 *                    usdc:string, usdcCollateralIndex:number}>}
 */
export async function getDelegateAddress() {
  return signalsApi.getDelegateAddress();
}

/* POST /enroll is deliberately NOT re-exported here.
   It used to forward to the transport's bare unsigned enroll — a probe the live
   engine answers with a 400, and a function db141f1 removed outright, so this
   wrapper had become a TypeError waiting for its first caller.
   Enrollment has exactly one implementation now:
   components/strategyautomation/enrollFlow.js (nonce → personal_sign → POST),
   reached through useEnrollment. Adding a second entry point here is what let
   the two surfaces drift the first time. */

/**
 * GET /status — the whole tab's state in one read: wallet values, delegate
 * status, risk config + allocations, open positions, and the RATIFIED §1b/§2/§3
 * additions. Read-only.
 * @returns {Promise<EngineStatus>}
 */
export async function getStatus() {
  return normalizeStatus(await signalsApi.getStatus());
}

/**
 * POST /config — the RATIFIED risk surface only. Anything not in this pick is
 * dropped before the request rather than sent and silently discarded server
 * side, which is the documented failure this client exists to stop repeating.
 * @param {{maxLeverage?:number, maxCollateralPctPerTrade?:number,
 *          pairAllowlist?:string[], enabled?:boolean}} cfg
 */
export async function setRiskConfig(cfg) {
  const c = cfg || {};
  return signalsApi.setConfig({
    maxLeverage: c.maxLeverage,
    maxCollateralPctPerTrade: c.maxCollateralPctPerTrade,
    pairAllowlist: Array.isArray(c.pairAllowlist) ? c.pairAllowlist : [],
    enabled: c.enabled,
  });
}

/**
 * POST /config { allocations } — the Phase-1 allocations patch. PERSISTENCE
 * ONLY: saved allocations do not size a single trade until the engine's own
 * audit-gated sizing flag is armed server-side (§1, PLAN). Callers must refetch
 * and compare the engine's echo — see allocationModel.compareAllocationEcho.
 *
 * ARC-50: the engine validates `allocations` as an ARRAY of { pair, pct } and
 * answers a map with `400 allocations must be an array`. Callers hold it as a
 * map keyed by wire key, so the conversion happens HERE, at the one wire
 * boundary every caller already goes through, rather than in each surface.
 * @param {Object<string, EngineAllocation>|Array<{pair:string,pct:number}>} allocations
 */
export async function setAllocations(allocations) {
  if (typeof signalsApi.setAllocations !== "function") {
    const err = new Error("This build's signals client has no allocations endpoint.");
    err.code = "UNSUPPORTED";
    throw err;
  }
  /* Already-array callers pass through untouched; a map is converted. */
  const wire = Array.isArray(allocations)
    ? allocations
    : toAllocationArray(allocations);
  return signalsApi.setAllocations(wire);
}

/** Does the transport expose the allocations patch at all? */
export const allocationsSupported = () => typeof signalsApi.setAllocations === "function";

/* ── the wallet registry (ARC-41 engine, ARC-46 tab) ───────────────────────
   Multi-wallet is how a member segments strategies across addresses — stake on
   one, trade from another — against a single member-level slot pool. Every
   wallet earns its place with its OWN signature; anything less would let a
   member claim a stranger's stake toward their own slots.

   NOTHING HERE SIGNS. The signature is obtained in the member's own wallet by
   the caller (useArbitrumWallet) and arrives as an opaque string. These
   functions carry it to the engine and shape what comes back. */

/** Does the transport expose the registry at all? False ⇒ the tab disables Add
 *  wallet with the reason rather than offering a button that cannot work. */
export const walletRegistrySupported = () =>
  typeof signalsApi.listWallets === "function" &&
  typeof signalsApi.attachWallet === "function";

const unsupportedRegistry = () => {
  const err = new Error("This build's signals client has no wallet registry endpoints.");
  err.code = "UNSUPPORTED_CLIENT";
  return err;
};

/**
 * GET /wallets → the member's verified wallets, normalized.
 * @returns {Promise<Array<{address:string,label:?string,isPrimary:boolean,
 *   verifiedAt:?number,chainId:?number,automatedPairs:string[]}>>}
 */
export async function getWallets() {
  if (!walletRegistrySupported()) throw unsupportedRegistry();
  return normalizeWallets(await signalsApi.listWallets());
}

/**
 * POST /wallets — attach a wallet the member has just proven they control.
 * The nonce and message come from GET /enroll/nonce and the signature from
 * their wallet; this function authors none of the three.
 * @returns {Promise<{created: boolean, wallets: Array}>}
 */
export async function attachWallet({ wallet, signature, nonce, label } = {}) {
  if (!walletRegistrySupported()) throw unsupportedRegistry();
  const res = await signalsApi.attachWallet({ wallet, signature, nonce, label });
  return { created: res?.created !== false, wallets: normalizeWallets(res) };
}

/** PATCH /wallets/:address — rename. Cosmetic; the engine acts on none of it. */
export async function renameWallet(address, label) {
  if (typeof signalsApi.setWalletLabel !== "function") throw unsupportedRegistry();
  return normalizeWallets(await signalsApi.setWalletLabel(address, label));
}

/** DELETE /wallets/:address — detach. The engine's 409s are member-facing next
 *  steps (deallocate first / this is your enrolled wallet) and reach the caller
 *  intact on err.body.error — see walletRegistry.classifyWalletError. */
export async function detachWallet(address) {
  if (typeof signalsApi.detachWallet !== "function") throw unsupportedRegistry();
  return normalizeWallets(await signalsApi.detachWallet(address));
}

/* ── PLAN STUBS — typed, and deliberately unreachable ──────────────────────
   §1 Phases 2 and 3 are PLAN, not RATIFIED: the endpoints do not exist, the
   preconditions are unmet, and arming is a human decision taken with the engine
   lane, never something a browser session initiates. These exist so the shapes
   are written down in one place and so a future caller fails loudly at the call
   site instead of inventing a fetch. Every one of them throws before doing
   anything. None of them imports or touches the transport. */

const planStub = (endpoint, section) => {
  const err = new Error(
    `${endpoint} is PLAN status in the engine contract (${section}) and is not callable from the app.`
  );
  err.code = "PLAN_NOT_RATIFIED";
  return err;
};

/**
 * @typedef {Object} ShadowSizingRow  §1 Phase 2 — the allocation-sized order
 *   logged BESIDE the fixed-size order the engine actually sent. Read-only
 *   observation; nothing changes on-chain.
 * @property {string} pair
 * @property {number} atMs
 * @property {number} fixedSizeUsd
 * @property {number} allocationSizeUsd
 * @property {boolean} matchedExpectation
 */

/**
 * TODO(engine, PLAN §1 Phase 2): shadow-sizing report. Not built engine-side.
 * @returns {Promise<ShadowSizingRow[]>}
 */
export async function getShadowSizingReport() {
  throw planStub("GET /shadow-sizing", "§1 Phase 2");
}

/**
 * TODO(engine, SUPERBETA-8): rotate the member's signal URL. The engine has no
 * rotate route — /enroll is idempotent and always returns the SAME credential,
 * so there is nothing this could call that would actually rotate anything.
 * Typed here so the wizard's "manage" affordance has a real shape to disable
 * against instead of a dead button, and so the first real caller fails loudly.
 * @returns {Promise<never>}
 */
export async function regenerateWebhook() {
  throw planStub("POST /enroll/rotate", "SUPERBETA-8");
}

/**
 * TODO(engine, SUPERBETA-8): revoke the member's signal URL so TradingView
 * alerts stop being accepted. No engine route exists.
 *
 * NOTE what this is NOT: revoking the on-chain trading delegate, which is
 * RATIFIED, already shipped, and is the control that actually stops trading —
 * see WalletSection's Revoke. This would only stop signals being accepted.
 * @returns {Promise<never>}
 */
export async function revokeWebhook() {
  throw planStub("POST /enroll/revoke", "SUPERBETA-8");
}

/**
 * TODO(engine, PLAN §1 Phase 3): arming allocation sizing.
 * Arming is scoped server-side by an explicit userId allowlist and is gated on
 * six unmet preconditions. It is NOT initiated from the browser, now or later —
 * this stub exists to type the shape and to fail loudly, and it must keep
 * throwing unconditionally.
 * @returns {Promise<never>}
 */
export async function armAllocationSizing() {
  throw planStub("POST /allocation-sizing/arm", "§1 Phase 3");
}

const engineApi = {
  isEngineConfigured,
  getDelegateAddress,
  getStatus,
  setRiskConfig,
  setAllocations,
  allocationsSupported,
  /* the wallet registry (ARC-46) */
  walletRegistrySupported,
  getWallets,
  attachWallet,
  renameWallet,
  detachWallet,
  normalizeWallets,
  normalizeStatus,
  isUnreachableError,
  isNotEnrolledError,
  /* PLAN — throw by design */
  getShadowSizingReport,
  armAllocationSizing,
  regenerateWebhook,
  revokeWebhook,
};

export default engineApi;
